import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary, btnSecondary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { useCanAdmin } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, Landmark } from "lucide-react";

const empty = { bank_name: "", account_no: "", ifsc: "", branch: "", account_holder: "Arjun Fintech LLP", active: true };

export default function CompanyBanks() {
  const canAdmin = useCanAdmin();
  const canManage = canAdmin("company-banks");
  const [banks, setBanks] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/company-banks").then((r) => setBanks(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setShow(true); };
  const openEdit = (b) => { setForm({ ...empty, ...b }); setEditId(b.id); setShow(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_name.trim() || !form.account_no.trim() || !form.ifsc.trim()) {
      toast.error("Bank Name, Account No and IFSC are required"); return;
    }
    try {
      if (editId) await api.put(`/company-banks/${editId}`, form);
      else await api.post("/company-banks", form);
      toast.success(editId ? "Bank updated" : "Bank added");
      setShow(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this bank account?")) return;
    try {
      await api.delete(`/company-banks/${id}`);
      toast.success("Bank removed"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="company-banks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Landmark size={24} className="text-emerald-600" /> Arjun Fintech Bank Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Company bank accounts where lenders remit our commission payouts.</p>
        </div>
        {canManage && <button data-testid="add-company-bank-button" onClick={openNew} className={btnPrimary}><Plus size={16} /> Add Bank Account</button>}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">Bank Name</th>
              <th className="text-left px-4 py-3">Account No</th>
              <th className="text-left px-4 py-3">IFSC</th>
              <th className="text-left px-4 py-3">Branch</th>
              <th className="text-left px-4 py-3">Account Holder</th>
              <th className="text-left px-4 py-3">Status</th>
              {canManage && <th className="text-right px-4 py-3">Action</th>}
            </tr>
          </thead>
          <tbody>
            {banks.map((b) => (
              <tr key={b.id} data-testid={`company-bank-row-${b.id}`} className="border-b border-slate-100 text-sm text-slate-700">
                <td className="px-4 py-3 font-medium text-slate-900">{b.bank_name}</td>
                <td className="px-4 py-3 font-mono">{b.account_no}</td>
                <td className="px-4 py-3 font-mono">{b.ifsc}</td>
                <td className="px-4 py-3">{b.branch || "—"}</td>
                <td className="px-4 py-3">{b.account_holder || "—"}</td>
                <td className="px-4 py-3"><span className={b.active ? "text-emerald-600" : "text-slate-400"}>● {b.active ? "Active" : "Inactive"}</span></td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`edit-company-bank-${b.id}`} onClick={() => openEdit(b)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Pencil size={14} /></button>
                    <button data-testid={`delete-company-bank-${b.id}`} onClick={() => del(b.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 ml-1"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
            {banks.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-12 text-center text-slate-400 text-sm">No bank accounts added yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Edit Bank Account" : "Add Bank Account"} testId="company-bank-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Bank Name *</label><input data-testid="cb-bank-name-input" required value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className={inputCls} placeholder="e.g. HDFC Bank" /></div>
            <div><label className={labelCls}>Account No *</label><input data-testid="cb-account-no-input" required value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} className={inputCls} placeholder="Bank account number" /></div>
            <div><label className={labelCls}>IFSC *</label><input data-testid="cb-ifsc-input" required value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className={inputCls} placeholder="e.g. HDFC0000123" /></div>
            <div><label className={labelCls}>Branch</label><input data-testid="cb-branch-input" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} className={inputCls} placeholder="Branch name / address" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Account Holder</label><input data-testid="cb-holder-input" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className={inputCls} placeholder="Registered account holder name" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input data-testid="cb-active-checkbox" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active</label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShow(false)} className={btnSecondary}>Cancel</button>
            <button data-testid="submit-company-bank-button" type="submit" className={btnPrimary}>{editId ? "Save" : "Add Bank"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
